import { Action, createReducer, on } from '@ngrx/store';
import * as ErrorActions from '../actions/error.actions';

export interface ErrorState {
  currentError: ErrorActions.ErrorInfo | null;
  errorHistory: ErrorActions.ErrorInfo[];
  isVisible: boolean;
}

const initialState: ErrorState = {
  currentError: null,
  errorHistory: [],
  isVisible: false,
};

const _errorReducer = createReducer(
  initialState,
  on(
    ErrorActions.GlobalError,
    (state, { error }): ErrorState => ({
      ...state,
      currentError: error,
      errorHistory: [error, ...state.errorHistory.slice(0, 9)],
      isVisible: true,
    }),
  ),
  on(ErrorActions.HttpError, (state, { httpError, actionType }): ErrorState => {
    const errorInfo: ErrorActions.ErrorInfo = {
      statusCode: httpError.status,
      message:
        httpError.error?.message || httpError.message || getDefaultErrorMessage(httpError.status),
      timestamp: new Date(),
      actionType,
      details: httpError.error,
    };
    return {
      ...state,
      currentError: errorInfo,
      errorHistory: [errorInfo, ...state.errorHistory.slice(0, 9)],
      isVisible: true,
    };
  }),
  on(
    ErrorActions.ClearCurrentError,
    (state): ErrorState => ({
      ...state,
      currentError: null,
      isVisible: false,
    }),
  ),
  on(
    ErrorActions.ClearAllErrors,
    (state): ErrorState => ({
      ...state,
      currentError: null,
      errorHistory: [],
      isVisible: false,
    }),
  ),
  on(
    ErrorActions.DismissError,
    (state, { timestamp }): ErrorState => ({
      ...state,
      errorHistory: state.errorHistory.filter(
        (error) => error.timestamp.getTime() !== timestamp.getTime(),
      ),
      currentError:
        state.currentError?.timestamp.getTime() === timestamp.getTime() ? null : state.currentError,
      isVisible:
        state.currentError?.timestamp.getTime() === timestamp.getTime() ? false : state.isVisible,
    }),
  ),
);

function getDefaultErrorMessage(statusCode: number): string {
  switch (statusCode) {
    case 400:
      return 'Bad Request - Please check your input';
    case 401:
      return 'Unauthorized - Please log in';
    case 403:
      return 'Forbidden - You do not have permission';
    case 404:
      return 'Not Found - The requested resource was not found';
    case 408:
      return 'Request Timeout - Please try again';
    case 409:
      return 'Conflict - The request could not be completed';
    case 422:
      return 'Unprocessable Entity - Invalid data provided';
    case 429:
      return 'Too Many Requests - Please wait before trying again';
    case 500:
      return 'Internal Server Error - Something went wrong on our end';
    case 502:
      return 'Bad Gateway - Service temporarily unavailable';
    case 503:
      return 'Service Unavailable - Please try again later';
    case 504:
      return 'Gateway Timeout - The request took too long';
    default:
      return `An error occurred (${statusCode}) - Please try again`;
  }
}

export const errorReducer = (state: ErrorState = initialState, action: Action): ErrorState =>
  _errorReducer(state, action);
